'use client'

import { useState } from 'react'
import { Bookmark, Check, Share2, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { useMonkeCatalog, useSavedMonkes } from '@/utils/hooks/useMonkeDiscovery'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import { COLOR_TRAIT, monkeFilterUrl } from '@/utils/nodemonkes'

export function saveBlob(blob: Blob, name: string) {
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url; a.download = name; a.click()
	setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export default function MonkeCollectorTools({ id, navigate, background, onBackground }: {
	id: string; navigate: (url: string) => void; background: string; onBackground: (value: string) => void
}) {
	const { byId, data } = useMonkeCatalog()
	const monke = byId.get(id)
	const { saved, toggle, error: saveError } = useSavedMonkes()
	const open = useInscriptionModal()
	const [message, setMessage] = useState('')
	const [downloading, setDownloading] = useState(false)
	const [copied, setCopied] = useState(false)
	const button = 'inline-flex min-h-10 items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary'
	async function download() {
		if (!monke) return
		setDownloading(true); setMessage('')
		try {
			const response = await fetch(`/api/nodemonkes/${monke.number}/image`)
			if (!response.ok) throw new Error('Image unavailable')
			const bitmap = await createImageBitmap(await response.blob())
			const canvas = document.createElement('canvas'); canvas.width = 1120; canvas.height = 1120
			const ctx = canvas.getContext('2d')
			if (!ctx) { bitmap.close(); throw new Error('Canvas unavailable') }
			if (background !== 'transparent') { ctx.fillStyle = background; ctx.fillRect(0, 0, 1120, 1120) }
			ctx.imageSmoothingEnabled = false; ctx.drawImage(bitmap, 0, 0, 1120, 1120); bitmap.close()
			const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
			if (!blob) throw new Error('Export failed')
			saveBlob(blob, `nodemonke-${monke.number}-${background === 'transparent' ? 'transparent' : background.slice(1)}.png`)
			setMessage('PNG downloaded · 1120 × 1120')
		} catch { setMessage('Image download failed. Please try again.') }
		finally { setDownloading(false) }
	}
	return <div className="space-y-3 border-b border-border p-4 sm:p-5">
		<div className="flex flex-wrap gap-2">
			<button type="button" className={button} aria-pressed={saved.includes(id)} onClick={() => toggle(id)}><Bookmark className="size-4" fill={saved.includes(id) ? 'currentColor' : 'none'} />{saved.includes(id) ? 'Saved' : 'Save'}</button>
			<button type="button" className={button} onClick={async () => {
				try { await navigator.clipboard.writeText(`${window.location.origin}/collection/nodemonkes?monke=${monke?.number || ''}`); setCopied(true); setMessage('Monke link copied') }
				catch { setMessage('Could not copy the link. Please try again.') }
			}} disabled={!monke}>{copied ? <Check className="size-4" /> : <Share2 className="size-4" />}Share</button>
			<button type="button" className={button} onClick={download} disabled={!monke || downloading}><Download className="size-4" />{downloading ? 'Exporting…' : 'Download PNG'}</button>
			{monke && data && <div className="ml-auto flex gap-1">
				{[-1, 1].map((direction) => {
					const target = data.items[(monke.number - 1 + direction + data.items.length) % data.items.length]
					return <button type="button" key={direction} aria-label={direction < 0 ? 'Previous Monke' : 'Next Monke'} className={button} onClick={() => open({ id: target.id, inscription: { id: target.id, content_type: 'image/png', meta: { name: `NodeMonke #${target.number}` }, collection: { slug: 'nodemonkes', name: 'NodeMonkes' } } })}>{direction < 0 ? <ChevronLeft className="size-4" /> : <ChevronRight className="size-4" />}</button>
				})}
			</div>}
		</div>
		<div className="flex flex-wrap items-center gap-2 text-xs">
			<span className="text-muted-foreground">Background</span>
			{[{ value: 'transparent', label: 'Original' }, { value: '#f7931a', label: 'Orange' }, { value: '#e8dcc4', label: 'Sand' }].map((option) => <button key={option.value} type="button" aria-pressed={background === option.value} onClick={() => onBackground(option.value)} className={`min-h-9 rounded-md border px-2.5 ${background === option.value ? 'border-primary text-primary' : 'border-border hover:bg-accent'}`}>{option.label}</button>)}
		</div>
		{monke && <div className="flex flex-wrap items-center gap-1.5"><span className="mr-1 text-xs text-muted-foreground">Explore colors</span>{monke.colors.map((color) => <button type="button" key={color} title={`Find Monkes with #${color}`} aria-label={`Find Monkes with color #${color}`} onClick={() => navigate(monkeFilterUrl([{ trait_type: COLOR_TRAIT, values: [color] }]))} className="size-8 rounded-md border border-foreground/20 ring-offset-background hover:ring-2 hover:ring-primary focus-visible:ring-2 focus-visible:ring-primary" style={{ backgroundColor: `#${color}` }} />)}</div>}
		{(message || saveError) && <p role="status" className="text-xs text-muted-foreground">{saveError || message}</p>}
	</div>
}
